// AICA Call Statistics Analytics Page
// Displays call volume, duration, answer rate, quality metrics with interactive charts
import { createFileRoute } from '@tanstack/react-router';
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHelp } from '@/components/help/PageHelp';
import { DashboardBody } from '@/components/layout/DashboardBody';
import { PeriodBar, type Period } from '@/components/layout/PeriodBar';
import { useExactReportWindow } from '@/hooks/useExactReportWindow';
import { ProjectMultiSelect } from '@/components/filters';
import {
  parseFlowReportSearch,
  type FlowReportSearch,
} from '@/components/analytics/flow/flow-report-search';
import { KpiCard } from '@/components/dashboard/kpi-card-v2';
import { DashboardPanel } from '@/components/dashboard/dashboard-panel';
import { BarChart } from '@/components/data';
import { cn, formatNumber, resolveLocale } from '@/lib/utils';
import { seriesStepLabel } from '@/lib/series-funnel';
import { formatMissedShare, missedSharePercent } from '@/lib/missed-share';
import { employeeRowKey } from '@/lib/employee-row-key';
import { FeatureGate } from '@/components/ui/FeatureGate';
import { BarChart3, Loader2, Phone } from 'lucide-react';
import { Breadcrumb, BreadcrumbItem } from '@/components/navigation/breadcrumb';
import { useCallStats, useSeriesFunnel } from '@/hooks/useCallStats';
import type { SeriesFunnelStep } from '@aica/api-types/schemas';

// ============================================================================
// HELPERS
// ============================================================================

function formatSeconds(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

// Maps backend duration-bin strings (RecordMetadataAnalyticsRepository) to i18n keys.
const DURATION_BIN_KEY: Record<string, string> = {
  '<60s': 'under60',
  '60-180s': 's60to180',
  '180-300s': 's180to300',
  '300-600s': 's300to600',
  '>600s': 'over600',
};

const colorBarMap: Record<string, string> = {
  success: 'bg-success',
  primary: 'bg-primary',
  warning: 'bg-warning',
  danger: 'bg-danger',
  info: 'bg-info',
  accent: 'bg-accent',
};

function ProgressBar({ percentage, color }: { percentage: number; color: string }) {
  return (
    <div className="h-[4px] w-full rounded-full bg-bg-elevated">
      <div
        className={cn(
          'h-full rounded-full transition-all duration-300',
          colorBarMap[color] ?? 'bg-primary'
        )}
        style={{ width: `${Math.min(percentage, 100)}%` }}
      />
    </div>
  );
}

function EmptyPanel({
  message,
  icon: Icon = Phone,
}: {
  message: string;
  icon?: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <Icon className="h-8 w-8 text-fg-muted mb-2 opacity-40" />
      <p className="text-sm text-fg-muted">{message}</p>
    </div>
  );
}

// ============================================================================
// CALL STATISTICS PAGE
// ============================================================================

const NO_LINK_SCOPE: FlowReportSearch = {};
const NO_CHANGE = () => {};

// Exported (not just used via `Route`) so it can be unit-tested directly,
// bypassing the FeatureGate/createFileRoute wiring at the bottom of this file.
export function CallStatsPage({
  search = NO_LINK_SCOPE,
  onSearchChange = NO_CHANGE,
}: {
  search?: FlowReportSearch;
  /** Merge a change into the address (replacing the history entry). */
  onSearchChange?: (patch: Partial<FlowReportSearch>) => void;
}) {
  const { t } = useTranslation(['analyses', 'common']);
  const [activePeriod, setPeriod] = useState<Period>('week');
  // The window of the dashboard card that linked here wins until the first step.
  const { dateRange, navigation, onPeriodChange } = useExactReportWindow(
    search,
    onSearchChange,
    activePeriod,
    setPeriod
  );

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [seriesFilter, setSeriesFilter] = useState<'deal' | 'lead' | undefined>(
    undefined
  );

  // The page is about calls: chats do not count in its total, its hours or its
  // durations — unless the link of a dashboard card says its channel was
  // chats or both (`all` — no narrowing). The flow's missed-call definitions
  // are calls-only anyway.
  const channel = search.channel === 'all' ? undefined : (search.channel ?? 'call');
  // Missed calls and call series are about calls: a chats-only report has
  // nothing to say about them, so it does not show their panels at all.
  const callsOnlyPanels = channel !== 'chat';
  // What the totals count: calls on the page's own channel, conversations once
  // chats are in (a card's link with the channel filter off or on «Chats»).
  const unit = channel === 'call' ? 'calls' : 'conversations';
  const countLabel = (count: number) =>
    t(unit === 'calls' ? 'callStats.callsCount' : 'callStats.conversationsCount', {
      count,
    });
  const { data, isLoading, isFetching, error, refetch } = useCallStats({
    period: activePeriod,
    periodStart: dateRange.periodStart,
    periodEnd: dateRange.periodEnd,
    projectIds: search.projectIds,
    channel,
    includeDemo: search.demo,
  });
  const { data: funnelData } = useSeriesFunnel({
    period: activePeriod,
    periodStart: dateRange.periodStart,
    periodEnd: dateRange.periodEnd,
    entityType: seriesFilter,
    projectIds: search.projectIds,
    includeDemo: search.demo,
    enabled: callsOnlyPanels,
  });

  const filters = (
    <div className="flex flex-wrap items-center justify-end gap-2">
      {search.channel === 'chat' || search.channel === 'all' ? (
        <span className="text-xs text-fg-muted">
          {t(`callStats.channelNote.${search.channel}`)}
        </span>
      ) : null}
      <ProjectMultiSelect
        className="w-44"
        selectedProjectIds={search.projectIds ?? []}
        onChange={(ids) =>
          onSearchChange({ projectIds: ids.length > 0 ? ids : undefined })
        }
      />
    </div>
  );

  // Kept on screen in every state: a project picked in the filter changes the
  // query, and the open dropdown must not vanish with the spinner — nor the
  // filter with an error, or a bad project in the address could not be cleared.
  const periodBar = (
    <PeriodBar
      activePeriod={activePeriod}
      onPeriodChange={onPeriodChange}
      navigation={navigation}
      isLoading={isFetching && !isLoading}
      actions={filters}
    />
  );

  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    void refetch().finally(() => setIsRefreshing(false));
  }, [refetch]);

  const renderStepLabel = (step: SeriesFunnelStep): string => {
    const label = seriesStepLabel(step);
    if (label.kind === 'single') {
      return t('callStats.series.callNumber', { number: label.number });
    }
    if (label.kind === 'open') {
      return t('callStats.series.callRangeOpen', { from: label.from });
    }
    return t('callStats.series.callRange', { from: label.from, to: label.to });
  };

  // Compute KPI values from response
  const totalCalls = data?.total ?? 0;
  const avgDuration = data?.avgDurationSeconds ?? 0;
  const avgQualityScore = data?.avgQualityScore ?? null;
  // The missed share is the server's (the flow's definitions): missed inbound
  // calls of all inbound calls. A response without the block (an older edge)
  // reads as «—», never as 0.
  const missedShare = data?.missed ? data.missed.share : null;
  const missedRate = missedSharePercent(missedShare);
  // The rest of the same inbound calls, from the shown share: the two cards add up to 100.
  const answerRate =
    missedRate !== null ? Math.round((100 - missedRate) * 10) / 10 : null;
  const unknownDirectionMissed = data?.missed?.unknownDirection ?? 0;
  const comparison = data?.comparison;

  if (isLoading) {
    return (
      <DashboardBody
        title={t('callStats.title')}
        subtitle={t('callStats.subtitle')}
        icon={BarChart3}
        breadcrumbs={
          <Breadcrumb>
            <BreadcrumbItem href="/">Aelo</BreadcrumbItem>
            <BreadcrumbItem current>{t('callStats.breadcrumb')}</BreadcrumbItem>
          </Breadcrumb>
        }
      >
        {periodBar}
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-fg-muted" />
        </div>
      </DashboardBody>
    );
  }

  if (error) {
    return (
      <DashboardBody
        title={t('callStats.title')}
        subtitle={t('callStats.subtitle')}
        icon={BarChart3}
        breadcrumbs={
          <Breadcrumb>
            <BreadcrumbItem href="/">Aelo</BreadcrumbItem>
            <BreadcrumbItem current>{t('callStats.breadcrumb')}</BreadcrumbItem>
          </Breadcrumb>
        }
      >
        {periodBar}
        <div className="flex items-center justify-center py-16 text-sm text-danger">
          {t('callStats.error.loadFailed')}
        </div>
      </DashboardBody>
    );
  }

  if (!isLoading && !error && data && data.total === 0) {
    return (
      <DashboardBody
        title={t('callStats.title')}
        subtitle={t('callStats.subtitle')}
        icon={BarChart3}
        breadcrumbs={
          <Breadcrumb>
            <BreadcrumbItem href="/">Aelo</BreadcrumbItem>
            <BreadcrumbItem current>{t('callStats.breadcrumb')}</BreadcrumbItem>
          </Breadcrumb>
        }
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
      >
        {periodBar}
        <div className="flex items-center justify-center py-16">
          <EmptyPanel message={t('callStats.empty.noData')} icon={BarChart3} />
        </div>
      </DashboardBody>
    );
  }

  return (
    <DashboardBody
      title={
        <span className="flex items-center gap-2">
          {t('callStats.title')}
          <PageHelp pageId="trends" />
        </span>
      }
      subtitle={t('callStats.subtitle')}
      icon={BarChart3}
      breadcrumbs={
        <Breadcrumb>
          <BreadcrumbItem href="/">Aelo</BreadcrumbItem>
          <BreadcrumbItem current>{t('callStats.breadcrumb')}</BreadcrumbItem>
        </Breadcrumb>
      }
      onRefresh={handleRefresh}
      isRefreshing={isRefreshing}
      showExport
    >
      {periodBar}

      {/* KPI Row */}
      <div
        className={cn(
          'grid grid-cols-2 gap-3 mb-4',
          callsOnlyPanels ? 'md:grid-cols-5' : 'md:grid-cols-3'
        )}
      >
        <KpiCard
          label={t(
            unit === 'calls'
              ? 'callStats.kpi.totalCalls'
              : 'callStats.kpi.totalConversations'
          )}
          value={formatNumber(totalCalls)}
          trend={
            comparison?.totalGrowthPercent != null
              ? {
                  value: comparison.totalGrowthPercent,
                  direction: comparison.totalGrowthPercent >= 0 ? 'up' : 'down',
                }
              : undefined
          }
          color="primary"
        />
        <KpiCard
          label={t('callStats.kpi.avgDuration')}
          value={formatSeconds(avgDuration)}
          trend={
            comparison?.durationChangePercent != null
              ? {
                  value: comparison.durationChangePercent,
                  direction: comparison.durationChangePercent >= 0 ? 'up' : 'down',
                  label: '%',
                }
              : undefined
          }
          color="primary"
        />
        {callsOnlyPanels && (
          <>
            <KpiCard
              label={t('callStats.kpi.answerRate')}
              value={formatMissedShare(t, answerRate)}
              color={
                answerRate === null
                  ? 'primary'
                  : answerRate >= 80
                    ? 'success'
                    : answerRate >= 60
                      ? 'warning'
                      : 'danger'
              }
            />
            <KpiCard
              label={t('callStats.kpi.missedRate')}
              value={formatMissedShare(t, missedRate)}
              color={
                missedRate === null
                  ? 'primary'
                  : missedRate <= 10
                    ? 'success'
                    : missedRate <= 20
                      ? 'warning'
                      : 'danger'
              }
            />
          </>
        )}
        <KpiCard
          label={t('callStats.kpi.avgScore')}
          value={avgQualityScore !== null ? String(avgQualityScore) : '—'}
          color={
            avgQualityScore === null
              ? 'primary'
              : avgQualityScore >= 70
                ? 'success'
                : avgQualityScore >= 50
                  ? 'warning'
                  : 'danger'
          }
        />
      </div>
      {callsOnlyPanels && unknownDirectionMissed > 0 && (
        <p
          className="text-xs text-fg-muted mb-4"
          data-testid="call-stats-unknown-direction"
        >
          {t('callStats.kpi.unknownDirectionNote', { count: unknownDirectionMissed })}
        </p>
      )}

      {/* Row 1: Hourly Distribution + Agent Work Time */}
      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4 mb-4">
        <DashboardPanel title={t('callStats.panels.hourlyDistribution')}>
          {data?.byHour && data.byHour.length > 0 ? (
            <BarChart
              data={data.byHour.map((h) => ({ hour: `${h.hour}:00`, calls: h.count }))}
              xKey="hour"
              bars={[
                {
                  key: 'calls',
                  name: t(
                    unit === 'calls'
                      ? 'callStats.callsLabel'
                      : 'callStats.conversationsLabel'
                  ),
                  color: 'teal',
                },
              ]}
              height={260}
              showLegend={false}
            />
          ) : (
            <EmptyPanel message={t('callStats.empty.noHourlyData')} />
          )}
        </DashboardPanel>

        <DashboardPanel title={t('callStats.panels.agentWorkTime')}>
          {data?.agentWorkTime && data.agentWorkTime.length > 0 ? (
            <div className="-m-4">
              <table className="w-full">
                <tbody>
                  {data.agentWorkTime.slice(0, 10).map((agent) => (
                    <tr
                      key={employeeRowKey(agent)}
                      className="border-b border-border last:border-0 hover:bg-bg-elevated transition-colors"
                    >
                      <td className="px-4 py-3 text-sm font-medium text-fg">
                        {agent.agentName}
                      </td>
                      <td className="px-4 py-3 font-mono text-sm text-right">
                        {formatSeconds(agent.totalDurationSeconds)}
                      </td>
                      <td className="px-4 py-3 text-xs text-success text-right">
                        {countLabel(agent.recordCount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyPanel message={t('callStats.empty.noAgentData')} />
          )}
        </DashboardPanel>
      </div>

      {/* Row 2: Direction + Duration + Series Funnel */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        {/* By Direction */}
        <DashboardPanel title={t('callStats.panels.byDirection')}>
          {data?.byDirection && data.byDirection.length > 0 ? (
            <div className="space-y-3">
              {data.byDirection.map((d: { direction: string; count: number }) => {
                const pct = totalCalls > 0 ? (d.count / totalCalls) * 100 : 0;
                return (
                  <div key={d.direction}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-fg">
                        {t(`callStats.directions.${d.direction}`, d.direction)}
                      </span>
                      <span className="font-mono text-sm tabular-nums text-fg">
                        {pct.toFixed(0)}%
                      </span>
                    </div>
                    <ProgressBar
                      percentage={pct}
                      color={
                        d.direction === 'missed_inbound'
                          ? 'danger'
                          : d.direction === 'missed_outbound' ||
                              d.direction === 'missed_unknown'
                            ? 'warning'
                            : 'primary'
                      }
                    />
                    <p className="text-micro text-fg-muted mt-0.5">
                      {countLabel(d.count)}
                    </p>
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyPanel message={t('callStats.empty.noDirectionData')} />
          )}
        </DashboardPanel>

        {/* Duration Distribution */}
        <DashboardPanel title={t('callStats.panels.durationDistribution')}>
          {data?.durationBins && data.durationBins.length > 0 ? (
            (() => {
              const binTotal = data.durationBins.reduce(
                (sum: number, b: { count: number }) => sum + b.count,
                0
              );
              return (
                <div className="space-y-3">
                  {/* Total sample size — each bar already shows its own
                      count, but the aggregate discloses how small (or
                      large) the whole distribution is at a glance. */}
                  <p className="text-xs text-fg-muted">{countLabel(binTotal)}</p>
                  {data.durationBins.map((bin: { bin: string; count: number }) => {
                    const pct = binTotal > 0 ? (bin.count / binTotal) * 100 : 0;
                    return (
                      <div key={bin.bin}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-fg">
                            {t(
                              `callStats.durationBins.${DURATION_BIN_KEY[bin.bin] ?? ''}`,
                              bin.bin
                            )}
                          </span>
                          <span className="font-mono text-sm tabular-nums text-fg">
                            {pct.toFixed(0)}%
                          </span>
                        </div>
                        <ProgressBar percentage={pct} color="primary" />
                        <p className="text-micro text-fg-muted mt-0.5">
                          {countLabel(bin.count)}
                        </p>
                      </div>
                    );
                  })}
                </div>
              );
            })()
          ) : (
            <EmptyPanel message={t('callStats.empty.noDurationData')} />
          )}
        </DashboardPanel>

        {callsOnlyPanels && (
          <>
            {/* Series Funnel */}
            <DashboardPanel title={t('callStats.panels.seriesFunnel')}>
              {funnelData?.steps && funnelData.steps.length > 0 ? (
                <div className="space-y-3">
                  {/* Entity type toggle */}
                  <div className="flex gap-1 mb-2">
                    {(['all', 'deal', 'lead'] as const).map((type) => (
                      <button
                        key={type}
                        className={cn(
                          'px-2 py-1 text-xs rounded transition-colors',
                          (
                            type === 'all'
                              ? seriesFilter === undefined
                              : seriesFilter === type
                          )
                            ? 'bg-primary text-bg'
                            : 'bg-bg-elevated text-fg-muted hover:text-fg'
                        )}
                        onClick={() => setSeriesFilter(type === 'all' ? undefined : type)}
                      >
                        {type === 'all'
                          ? t('common:common.all', 'All')
                          : t(`callStats.series.${type}`, type)}
                      </button>
                    ))}
                  </div>

                  {funnelData.steps.map((step) => (
                    <div key={step.seriesNumber}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-fg">{renderStepLabel(step)}</span>
                        <span className="font-mono text-sm tabular-nums text-fg">
                          {step.count ?? 0}
                        </span>
                      </div>
                      <ProgressBar percentage={step.percentage} color="primary" />
                      {step.dropOffRate > 0 && (
                        <p className="text-micro text-warning mt-0.5">
                          {t('callStats.series.dropOff', {
                            rate: step.dropOffRate.toFixed(0),
                          })}
                        </p>
                      )}
                    </div>
                  ))}

                  {/* Summary stats */}
                  <div className="mt-4 pt-3 border-t border-border grid grid-cols-2 gap-2">
                    <div>
                      <p className="text-micro text-fg-muted">
                        {t('callStats.series.totalSeries')}
                      </p>
                      <p className="font-mono text-sm text-fg">
                        {funnelData.totalSeries}
                      </p>
                    </div>
                    <div>
                      <p className="text-micro text-fg-muted">
                        {t('callStats.series.avgCallsPerSeries')}
                      </p>
                      <p className="font-mono text-sm text-fg">
                        {(funnelData.avgCallsPerSeries ?? 0).toFixed(1)}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <EmptyPanel message={t('callStats.empty.noFunnelData')} />
              )}
            </DashboardPanel>
          </>
        )}
      </div>

      {/* Row 3: Project Breakdown */}
      {data?.byProject && data.byProject.length > 0 && (
        <div className="mb-4">
          <DashboardPanel title={t('callStats.panels.byProject', 'By Project')}>
            <div className="-m-4">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border text-xs text-fg-muted">
                    <th className="px-4 py-2 text-left font-medium">
                      {t('callStats.table.project', 'Project')}
                    </th>
                    <th className="px-4 py-2 text-right font-medium">
                      {t(
                        unit === 'calls'
                          ? 'callStats.table.calls'
                          : 'callStats.table.conversations'
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.byProject.map(
                    (proj: { projectId: string; projectName: string; count: number }) => (
                      <tr
                        key={proj.projectId}
                        className="border-b border-border last:border-0 hover:bg-bg-elevated transition-colors"
                      >
                        <td className="px-4 py-3 text-sm font-medium text-fg">
                          {proj.projectName}
                        </td>
                        <td className="px-4 py-3 font-mono text-sm text-right">
                          {proj.count}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          </DashboardPanel>
        </div>
      )}

      {/* Row 4: Daily Trend */}
      {data?.dailyTrend && data.dailyTrend.length > 0 && (
        <DashboardPanel title={t('callStats.panels.dailyTrend', 'Daily Trend')}>
          <BarChart
            data={data.dailyTrend.map((d: { day: string; count: number }) => ({
              // The bucket is already the viewer's day (tzOffset); read it
              // in UTC, or west of Greenwich every bar shows the day before.
              date: new Date(d.day).toLocaleDateString(resolveLocale(), {
                weekday: 'short',
                day: 'numeric',
                timeZone: 'UTC',
              }),
              calls: d.count,
            }))}
            xKey="date"
            bars={[
              {
                key: 'calls',
                name: t(
                  unit === 'calls'
                    ? 'callStats.callsLabel'
                    : 'callStats.conversationsLabel'
                ),
                color: 'teal',
              },
            ]}
            height={200}
            showLegend={false}
          />
        </DashboardPanel>
      )}
    </DashboardBody>
  );
}

export const Route = createFileRoute('/analytics/call-stats')({
  validateSearch: (search: Record<string, unknown>): FlowReportSearch =>
    parseFlowReportSearch(search),
  component: function CallStatsPageGated() {
    const search = Route.useSearch();
    const navigate = Route.useNavigate();
    const onSearchChange = useCallback(
      (patch: Partial<FlowReportSearch>) =>
        void navigate({
          search: (previous) => ({ ...previous, ...patch }),
          replace: true,
        }),
      [navigate]
    );
    return (
      <FeatureGate flag="enableAnalytics">
        <CallStatsPage search={search} onSearchChange={onSearchChange} />
      </FeatureGate>
    );
  },
});
